<template>
  <div>
    <div class="flex space-x-4 items-end">
      <BaseFormGroup>
        <BaseFormLabel> Date range </BaseFormLabel>
        <div>
          <BaseFormSelect
            v-model="dateRange"
            :options="[
              { label: 'Preset', value: 'preset' },
              { label: 'Custom', value: 'custom' },
            ]"
            required
            class="bg-white"
          />
        </div>
      </BaseFormGroup>

      <BaseFormGroup v-if="dateRange === 'preset'">
        <BaseFormLabel> Date preset </BaseFormLabel>
        <div>
          <BaseFormSelect
            v-model="query.datePreset"
            :options="datePresetOptions"
            required
            class="bg-white"
          />
        </div>
      </BaseFormGroup>

      <template v-if="dateRange === 'custom'">
        <BaseFormGroup>
          <BaseFormLabel> Date from </BaseFormLabel>
          <div>
            <BaseFormInput v-model="query.since" type="date" class="bg-white" />
          </div>
        </BaseFormGroup>

        <BaseFormGroup>
          <BaseFormLabel> Date to </BaseFormLabel>
          <div>
            <BaseFormInput v-model="query.until" type="date" class="bg-white" />
          </div>
        </BaseFormGroup>
      </template>

      <BaseFormGroup>
        <BaseFormLabel> Metrics </BaseFormLabel>
        <div class="p-px">
          <FacebookPageInsightMetricSelector
            :period="query.aggregatePeriod"
            v-model="query.metrics"
          />
        </div>
      </BaseFormGroup>

      <div class="p-px">
        <BaseButton primary class="h-10" :disabled="query.metrics.length === 0" @click="applyQuery">
          Submit
        </BaseButton>
      </div>

      <div class="p-px">
        <BaseButton ghost class="h-10" @click="resetQuery"> Reset </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import {
  DAY,
  LAST_30D,
  PAGE_TOTAL_ACTIONS,
  PAGE_POST_ENGAGEMENTS,
  PAGE_IMPRESSIONS,
  PAGE_POSTS_IMPRESSIONS,
  PAGE_ACTIONS_POST_REACTIONS_TOTAL,
  PAGE_FANS,
  datePresetOptions,
  periodOptions,
  PAGE_FAN_ADDS,
  PAGE_FAN_REMOVES,
  PAGE_FAN_ADDS_UNIQUE,
  PAGE_ENGAGED_USERS,
  PAGE_VIEWS_TOTAL,
} from '@/utils/facebook-page-insights-query-params';
import FacebookPageInsightMetricSelector from './facebook-page-insight-metric-selector.vue';

const PRESET = 'preset';
const DEFAULT_DATE_RANGE = PRESET;
const DEFAULT_QUERY_AGGREGATE_PERIOD = DAY;
const DEFAULT_QUERY_DATE_PRESET = LAST_30D;
const MAX_CUSTOM_DATE_RANGE_IN_DAYS = 90;

const areDatesValid = (dates) => dates.every((date) => moment(date).isValid());

const isValidMaxDateDiff = (dateSince, dateUntil) =>
  dateUntil.diff(dateSince, 'days') <= MAX_CUSTOM_DATE_RANGE_IN_DAYS;

const DEFAULT_METRICS = [
  PAGE_FANS,
  PAGE_FAN_ADDS,
  PAGE_FAN_REMOVES,
  PAGE_FAN_ADDS_UNIQUE,
  PAGE_ENGAGED_USERS,
  PAGE_POST_ENGAGEMENTS,
  PAGE_IMPRESSIONS,
  PAGE_TOTAL_ACTIONS,
  PAGE_POSTS_IMPRESSIONS,
  PAGE_ACTIONS_POST_REACTIONS_TOTAL,
  PAGE_VIEWS_TOTAL,
];

export default {
  components: {
    FacebookPageInsightMetricSelector,
  },
  data() {
    return {
      dateRange: DEFAULT_DATE_RANGE,
      query: {
        aggregatePeriod: DEFAULT_QUERY_AGGREGATE_PERIOD,
        datePreset: DEFAULT_QUERY_DATE_PRESET,
        since: null,
        until: null,
        metrics: [...DEFAULT_METRICS],
      },
    };
  },
  computed: {
    aggregatePeriodOptions() {
      return periodOptions;
    },
    datePresetOptions() {
      return datePresetOptions;
    },
  },
  watch: {
    'query.aggregatePeriod': {
      handler() {
        this.query.metrics = [];
      },
    },
    dateRange: {
      handler(range) {
        if (range === 'custom') {
          this.query.datePreset = null;
        } else {
          this.query.datePreset = DEFAULT_QUERY_DATE_PRESET;
          this.query.since = null;
          this.query.until = null;
        }
      },
    },
    'query.since': {
      handler(since) {
        const dateUntil = moment(this.query.until);
        const dateSince = moment(since);

        if (!areDatesValid([dateUntil, dateSince])) {
          return;
        }

        if (!isValidMaxDateDiff(dateSince, dateUntil)) {
          this.query.until = dateSince
            .add(MAX_CUSTOM_DATE_RANGE_IN_DAYS, 'days')
            .format('YYYY-MM-DD');
          return;
        }

        if (dateSince.isAfter(dateUntil)) {
          this.query.until = dateSince.format('YYYY-MM-DD');
        }
      },
    },
    'query.until': {
      handler(until) {
        const dateUntil = moment(until);
        const dateSince = moment(this.query.since);

        if (!areDatesValid([dateUntil, dateSince])) {
          return;
        }

        if (!isValidMaxDateDiff(dateSince, dateUntil)) {
          this.query.since = dateUntil
            .subtract(MAX_CUSTOM_DATE_RANGE_IN_DAYS, 'days')
            .format('YYYY-MM-DD');
          return;
        }

        if (dateUntil.isBefore(dateSince)) {
          this.query.since = dateUntil.format('YYYY-MM-DD');
        }
      },
    },
  },
  created() {
    this.applyQuery();
  },
  methods: {
    resetQuery() {
      this.query.aggregatePeriod = DEFAULT_QUERY_AGGREGATE_PERIOD;
      this.query.datePreset = DEFAULT_QUERY_DATE_PRESET;
      this.query.since = null;
      this.query.until = null;
      this.query.metrics = [...DEFAULT_METRICS];

      this.applyQuery();
    },
    applyQuery() {
      this.$emit('query', {
        period: this.query.aggregatePeriod,
        ...(this.query.datePreset
          ? {
              date_preset: this.query.datePreset,
            }
          : {
              since: this.query.since,
              until: this.query.until,
            }),
        metric: this.query.metrics.join(','),
      });
    },
  },
};
</script>
